"use client";
import { Toast as ArkToast, useToastContext } from "@ark-ui/react/toast";
import {
  IconCheckCircleFill,
  type IconComponent,
  IconInfoFill,
  IconWarningFill,
  IconWarningOctagonFill,
  IconX,
} from "@decibeltrade/react-icons";
import { ReactNode, useEffect, useState } from "react";
import { css, cva, cx } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { Button } from "../buttons";
import { Spinner } from "../spinner";

/** The toast stays open, paused and showing a spinner, until `onClick` settles, then dismisses. */
interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export const toastVariants = ["info", "error", "warning", "success", "loading"] as const;
export type ToastVariant = (typeof toastVariants)[number];

export interface ToastProps {
  /** The metadata of the toast message. */
  metadata?: Record<string, unknown>;
  /** The variant of the toast message. */
  variant: ToastVariant;
  /** Optional icon override. Falls back to the variant's default icon when omitted. */
  icon?: IconComponent;
  /** Optional className appended to the icon. Useful for color overrides when paired with a custom `icon`. */
  iconClassName?: string;
  /** The title of the toast message. */
  title: string;
  /** The description of the toast message to be displayed below the title. */
  description: string | (() => ReactNode);
  /** Optional click handler for the entire toast. */
  onClick?: () => void;
  action?: ToastAction;
}

/**
 * A message that appears on the screen to provide feedback on an action, or provide a notification
 * of an event that has occurred.
 */
export function Toast({
  variant,
  icon,
  iconClassName,
  title,
  description,
  onClick,
  action,
}: ToastProps) {
  const Icon = icon ?? iconMap[variant];
  const handleClick = onClick
    ? (e: React.MouseEvent) => {
        // Don't trigger onClick if clicking the close button
        if ((e.target as HTMLElement).closest('[data-part="close-trigger"]')) {
          return;
        }
        onClick();
      }
    : undefined;

  return (
    <ArkToast.Root
      className={toastStyles}
      onClick={handleClick}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      <Icon className={cx(iconStyles({ variant }), iconClassName)} />
      <div className={stack({ gap: "4" })}>
        <ArkToast.Title className={css({ textStyle: "body.md", color: "toast.text.title" })}>
          {title}
        </ArkToast.Title>
        <ArkToast.Description
          className={css({ textStyle: "highlight.sm", color: "toast.text.description" })}
        >
          {typeof description === "function" ? description() : description}
        </ArkToast.Description>
        {action && <ToastActionButton action={action} />}
      </div>
      <ArkToast.CloseTrigger className={css({ alignSelf: "start", cursor: "pointer" })}>
        <IconX className={css({ color: "icon.primary", h: "16", w: "16", m: "[2px]" })} />
      </ArkToast.CloseTrigger>
    </ArkToast.Root>
  );
}

function ToastActionButton({ action }: { action: ToastAction }) {
  const toast = useToastContext();
  const [loading, setLoading] = useState(false);
  // Hover and focus changes resume every toast in the group; re-pause so the
  // timer can't dismiss the toast while the action is still running.
  const { paused, pause } = toast;
  useEffect(() => {
    if (loading && !paused) pause();
  }, [loading, paused, pause]);
  const handleClick = async () => {
    setLoading(true);
    toast.pause();
    try {
      await action.onClick();
    } catch {
      // Actions own their error UI; this only keeps a rejection from going unhandled.
    } finally {
      toast.dismiss();
    }
  };
  return (
    <Button
      variant="secondary"
      size="sm"
      loading={loading}
      aria-busy={loading || undefined}
      onClick={() => void handleClick()}
      className={css({ alignSelf: "start", mt: "4" })}
    >
      {action.label}
    </Button>
  );
}

const iconMap: Record<ToastVariant, IconComponent> = {
  info: IconInfoFill,
  error: IconWarningOctagonFill,
  warning: IconWarningFill,
  success: IconCheckCircleFill,
  loading: Spinner,
};

const toastStyles = css({
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr) auto",
  columnGap: "8",
  rounded: "300",
  bg: "toast.background",
  borderColor: "toast.border",
  borderWidth: "100",
  borderStyle: "solid",
  p: "12",
  pb: "16",
  h: "var(--height)",
  w: "[calc(100vw - 32px)]",
  maxW: "[356px]",
  shadow: "default",
  // Keep overflow visible so Ark/Zag ghost hit-areas can prevent hover collapse jitter.
  overflow: "visible",

  // Positioning styles
  position: "relative",
  scale: "var(--scale)",
  translate: "[var(--x) var(--y) 0]",
  opacity: "var(--opacity)",
  willChange: "translate, opacity, scale, height",
  zIndex: "var(--z-index)",
  transitionDuration: "100",
  transitionProperty: "[translate, scale, opacity, height]",
  transitionTimingFunction: "default",
});

const iconStyles = cva({
  base: {
    h: "20",
    w: "20",
  },
  variants: {
    variant: {
      info: {
        color: "icon.primary",
      },
      error: {
        color: "icon.negative",
      },
      warning: {
        color: "icon.warning",
      },
      success: {
        color: "icon.positive",
      },
      loading: {
        color: "icon.primary",
      },
    },
  },
});
